import React from "react";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
} from "@fluentui/react-components";
import { Info20Filled } from "@fluentui/react-icons/svg/info";
import { useTranslation } from "react-i18next";
import { isConnectionSyncDisabled } from "@onetasklist/shared";

interface ConnectionSyncInfoPopoverProps {
  lastFullSync?: Date | string;
  lastSync?: Date | string;
  message?: string;
  disableUntil?: Date | string;
  disabledReason?: string;
}

const formatDateTime = (value: Date | string): string =>
  (value instanceof Date ? value : new Date(value)).toLocaleString();

export const ConnectionSyncInfoPopover: React.FC<
  ConnectionSyncInfoPopoverProps
> = ({ lastFullSync, lastSync, message, disableUntil, disabledReason }) => {
  const { t } = useTranslation();

  // `disabledReason` is left behind after a connection recovers, so the disabled state comes from
  // the dates: an unexpired window with no newer sync — see isConnectionSyncDisabled.
  const isDisabled = isConnectionSyncDisabled(disableUntil, lastSync);

  const hasInfo = !!lastFullSync || !!lastSync || !!message || isDisabled;

  if (!hasInfo) {
    return null;
  }

  const neverLabel = t("board.connections.catalogue.configure.neverSynced", {
    defaultValue: "Never",
  });

  return (
    <Popover withArrow positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          size="medium"
          icon={<Info20Filled />}
          aria-label={t(
            "board.connections.catalogue.configure.syncInfoAria",
            { defaultValue: "Sync details" },
          )}
          className="configure-connection-view__sync-info-trigger"
          style={{
            color: isDisabled
              ? "var(--colorStatusDangerBackground1, #c91f41)"
              : "var(--colorBrandForeground1, #0f6cbd)",
          }}
        />
      </PopoverTrigger>
      <PopoverSurface className="configure-connection-view__sync-info-surface">
        <Text weight="semibold" block size={200}>
          {t("board.connections.catalogue.configure.syncInfoTitle", {
            defaultValue: "Sync details",
          })}
        </Text>

        <div className="configure-connection-view__sync-info-row">
          <Text size={200} className="configure-connection-view__sync-info-label">
            {t("board.connections.catalogue.configure.lastFullSyncLabel", {
              defaultValue: "Last full sync",
            })}
          </Text>
          <Text size={200}>
            {lastFullSync ? formatDateTime(lastFullSync) : neverLabel}
          </Text>
        </div>

        <div className="configure-connection-view__sync-info-row">
          <Text size={200} className="configure-connection-view__sync-info-label">
            {t("board.connections.catalogue.configure.lastSyncLabel", {
              defaultValue: "Last sync",
            })}
          </Text>
          <Text size={200}>
            {lastSync ? formatDateTime(lastSync) : neverLabel}
          </Text>
        </div>

        {message && (
          <div className="configure-connection-view__sync-info-row">
            <Text size={200} className="configure-connection-view__sync-info-label">
              {t("board.connections.catalogue.configure.messageLabel", {
                defaultValue: "Message",
              })}
            </Text>
            <Text size={200}>{message}</Text>
          </div>
        )}

        {isDisabled && (
          <div className="configure-connection-view__sync-info-warning">
            {disableUntil && (
              <div className="configure-connection-view__sync-info-row">
                <Text
                  size={200}
                  className="configure-connection-view__sync-info-label"
                >
                  {t(
                    "board.connections.catalogue.configure.disabledUntilLabel",
                    { defaultValue: "Disabled until" },
                  )}
                </Text>
                <Text size={200}>{formatDateTime(disableUntil)}</Text>
              </div>
            )}
            {disabledReason && (
              <div className="configure-connection-view__sync-info-row">
                <Text
                  size={200}
                  className="configure-connection-view__sync-info-label"
                >
                  {t(
                    "board.connections.catalogue.configure.disabledReasonLabel",
                    { defaultValue: "Disabled reason" },
                  )}
                </Text>
                <Text size={200}>{disabledReason}</Text>
              </div>
            )}
          </div>
        )}
      </PopoverSurface>
    </Popover>
  );
};
